import {
  calculateTradePrice
} from "@dmf/calculator-library";
import moment from 'moment';
import { useEffect, useRef } from "react";
import appConfig from "../../../../appConfig";
import { renderErrorMessage } from "../../../../components/messages/errorMessage";
import TaxCaseStore from "../../../../contexts/taxCaseStore";
import TaxFlowStore, { TaxFlow } from "../../../../contexts/taxFlowStore";
import { EquipmentCatalog } from "../../../../types/newPriceTypes";
import { DynamicFee, initDynamicFee } from "../../../../types/taxTypes";
import { isValidNum } from "../../../../utilities/typeGuard";
import { calculateRegistrationFee, RegistrationFeeInput } from "./registrationFeeHelpers";
import { mapQuality } from "./util";
import { Strict } from "../../../../types/utilTypes";
import { safeNum } from "../../../../utilities/number";


export type RegulatedEquipmentMap = Record<string | "evaluationVehicle", EquipmentCatalog[]>
const projectedRegistrationFeeYear = 2027;

export default function useTaxFlowCalculations(optMode: boolean) {
  const { taxCase, isWhiteListedVehicle } = TaxCaseStore.useContainer();
  const { taxFlow, taxScenario, setTaxFlowSilent, includedAdverts, equipmentSums, regulatedEquipmentMap, setDirty } = TaxFlowStore.useContainer();

  // calculate date is either set here,
  // or if preassessment is sent, then the log date for that one,
  // or if its an optimiation, then its the log date of the time we sent the invoice
  const calcDateRef = useRef(new Date().toISOString());

  // ---------- New price total (canonical derived) ----------
  useEffect(() => {
    setTaxFlowSilent(prev => {
      const total = effectiveNewPriceTotal(prev);
      if (prev.newPrice?.newPrice === total) return prev;
      return { ...prev, newPrice: { ...prev.newPrice, newPrice: Math.ceil(total) } };
    });
  }, [
    taxFlow.newPrice?.newPriceWithoutEquipment,
    taxFlow.newPrice?.equipmentPrice,
    taxFlow.newPrice?.taxableValueNewPrice?.newPrice,
    taxFlow.newPrice?.newPriceType,
  ]);


  // ---------- Trade price: input builders ----------
  function buildTradePriceInput() {
    const v = taxCase.vehicle;
    if (!v?.firstRegDate) return null;
    if (!includedAdverts.length) return null;

    return {
      equipmentPrice: equipmentSums.evaluationVehicle ?? 0,
      firstRegDate: v.firstRegDate,
      mileage: v.mileage ?? 0,
      quality: mapQuality(v.qualityId ?? -1),
      specialUse: !!v.useCaseId && v.useCaseId !== 1,
      calcDate: calcDateRef.current,
      adverts: includedAdverts.map(ad => ({
        equipmentPrice: equipmentSums[ad.id] ?? 0,
        firstRegDateYear: moment(ad.firstRegDate).year(),
        mileage: ad.mileage ?? 0,
        salesPrice: ad.price?.amount ?? 0,
      })),
      regulatedAdverts: includedAdverts.map(ad => {
        if (!ad.config?.regulations?.output) {
          throw new Error(`${ad.id} - ${ad.name}: FEJL - Kunne ikke beregne prisniveau ---- ad.config?.regulations?.output is not an object. instead it was ${ad.config?.regulations?.output}. Contact Nicolai`)
        }
        if (!isValidNum(ad.config?.regulations?.output?.tradePriceLevel)) {
          throw new Error(`${ad.id} - ${ad.name}: FEJL - Kunne ikke beregne prisniveau ---- ad.config?.regulations?.output?.tradePriceLevel is not a valid number. instead it was type ${typeof ad.config?.regulations?.output?.tradePriceLevel}. Contact Nicolai`)
        }

        return {
          tradePriceLevel: ad.config?.regulations?.output?.tradePriceLevel ?? 0,
          ageDifference: moment(ad.firstRegDate).diff(v.firstRegDate, "year") ?? 0,
          ageRegulatedMileage: ad.config?.regulations?.output?.ageRegulatedMileage ?? 0,
          ageRegulatedMileageDifference: ad.config?.regulations?.output?.ageRegulatedMileageDifference ?? 0,
          ageRegulatedPrice: ad.config?.regulations?.output?.ageRegulatedPrice ?? 0,
          ageRegulatedPriceDifference: ad.config?.regulations?.output?.ageRegulatedPriceDifference ?? 0,
          unregulatedMileage: ad.mileage ?? 0,
          unregulatedPrice: ad.price?.amount ?? 0,
        }
      }),
    };
  }

  // ---------- Trade price: base calculation effect ----------
  useEffect(() => {
    try {
      const input = buildTradePriceInput();
      if (!input) return;
      const output = calculateTradePrice(input);

      setTaxFlowSilent(prev => ({
        ...prev,
        tradePrice: {
          ...prev.tradePrice,
          regulatedEquipmentMap,
          output,
        },
      }));
    } catch (error) {
      renderErrorMessage(error);
    }
  }, [
    taxCase.vehicle?.firstRegDate,
    taxCase.vehicle?.mileage,
    taxCase.vehicle?.qualityId,
    taxCase.vehicle?.useCaseId,
    includedAdverts,
    equipmentSums,
    regulatedEquipmentMap,
  ]);

  // ---------- Trade price: overrides effect ----------
  useEffect(() => {
    const output = taxFlow.tradePrice?.output;
    if (!output) return;
    const overrides = taxFlow.tradePrice?.overrides;

    try {
      const summary = handleTradePriceOverrides(output, overrides);

      setTaxFlowSilent(prev => ({
        ...prev,
        tradePrice: {
          ...prev.tradePrice,
          summary,
          tradePrice: prev.autoCalc?.tradePrice ? summary?.finalTradePrice : prev.tradePrice?.tradePrice,
        },
      }));
    } catch (error) {
      renderErrorMessage(error);
    }
  }, [
    taxFlow.tradePrice?.output?.result?.tradePrice,
    taxFlow.tradePrice?.overrides?.mileage,
    taxFlow.tradePrice?.overrides?.additionalRegulation?.amount,
    taxFlow.tradePrice?.overrides?.additionalRegulation?.note,
    taxFlow.autoCalc?.tradePrice,
  ]);

  // ---------- Registration fee effect ----------
  useEffect(() => {
    try {
      const vehicle = taxCase.vehicle
      const tradePrice = taxFlow.tradePrice?.tradePrice
      const newPrice = taxFlow.newPrice?.newPrice
      const taxableValue = taxFlow.newPrice?.taxableValue
      if (vehicle?.isNew && !isValidNum(taxableValue)) return
      if (!vehicle?.isNew && (!isValidNum(newPrice) || !isValidNum(tradePrice))) return
      if (!vehicle?.isNew && isValidNum(newPrice) && isValidNum(tradePrice) && newPrice < tradePrice) return

      const currentVersionYear = new Date().getFullYear() as RegistrationFeeInput['versionYear'];
      const input: RegistrationFeeInput = {
        emissionValue: vehicle?.emissionAmount,
        emissionUnitId: vehicle?.emissionUnitId,
        batteryCapacity: vehicle?.batteryCapacity,
        camperLivingArrangementCost: 0,
        electricConsumption: vehicle?.electricConsumption,
        electricRange: vehicle?.electricRange,
        isHighWeight: vehicle?.isHighWeight,
        isNew: vehicle?.isNew,
        newPrice,
        taxableValue: taxableValue,
        tradePrice,
        versionYear: currentVersionYear,
        vehicleTypeId: vehicle?.typeId
      }
      const output = calculateRegistrationFee(input)
      const regFee = isValidNum(output.result?.taxAfterAllDeductions) ? Math.ceil(output.result?.taxAfterAllDeductions) : null
      const projectedOutput = calculateRegistrationFee({
        ...input,
        versionYear: projectedRegistrationFeeYear,
      })
      const projectedRegFee = isValidNum(projectedOutput.result?.taxAfterAllDeductions)
        ? Math.ceil(projectedOutput.result?.taxAfterAllDeductions)
        : null
      setTaxFlowSilent((prev) => {
        return {
          ...prev,
          registrationFee: {
            ...prev.registrationFee,
            registrationFee: (!taxScenario?.flags?.optimization && prev.autoCalc?.registrationFee)
              ? regFee
              : prev.registrationFee?.registrationFee,
            output: output
          },
          projected: {
            ...prev.projected,
            estimatedRegistrationFee: projectedRegFee,
            calculation: projectedOutput,
          }

        }
      })
    } catch (error) {
      renderErrorMessage(error)
      console.error(error)
    }
  }, [
    taxCase.vehicle,
    taxFlow?.tradePrice?.tradePrice,
    taxFlow?.newPrice?.newPrice,
    taxFlow.autoCalc?.registrationFee,
    taxFlow.newPrice?.taxableValue,
    taxScenario?.flags?.optimization
  ])


  // ---------- taxOffer caseFee effect ----------
  useEffect(() => {
    if (optMode) return;
    const defaultFee = taxCase.defaultFee
    const zeroTaxFee = taxCase.zeroTaxFee;
    const dynamicFee = taxCase?.dynamicFee
    setTaxFlowSilent((prev) => {
      let caseFee = (defaultFee ?? 0) * 1.25
      const registrationFee = prev.registrationFee?.registrationFee ?? null;
      if (isValidNum(registrationFee)) {
        if (dynamicFee?.enabled) {
          const registrationFee = prev.registrationFee?.registrationFee ?? null;
          caseFee = calculateCaseFee(registrationFee, taxCase.dynamicFee)
        }
        if (registrationFee === 0) {
          caseFee = (zeroTaxFee ?? 0) * 1.25
        }
      }
      return {
        ...prev,
        offer: {
          ...prev.offer,
          caseFee: prev.autoCalc?.caseFee ? caseFee : prev.offer?.caseFee
        }
      }
    })
  }, [taxFlow.registrationFee?.registrationFee, taxFlow.autoCalc?.caseFee])


  // ---------- taxOffer collateralFee effect ----------
  const isWidgetCase = taxCase.caseSourceId === appConfig("widgetCaseSourceId");
  const shouldCalculateB2CCollateral = isWidgetCase && !taxCase.taxCaseValueConfig?.disableWidgetB2cCollateral.value
  const isElectric = !taxCase.vehicle?.emissionAmount;
  useEffect(() => {
    if (taxScenario?.flags?.optimization) {
      setTaxFlowSilent((prev) => {
        const estimatedCollateral = taxCase.scenarios.offer?.calculation?.offer?.collateral?.total ?? 0;
        const offerRegFee = taxCase.scenarios.offer?.calculation?.registrationFee?.registrationFee ?? 0
        const finalOfferRegFee = (offerRegFee || 0) + (estimatedCollateral || 0);

        const optimizationRegFee = prev.registrationFee?.registrationFee ?? 0;
        const collateral = isValidNum(finalOfferRegFee) ? finalOfferRegFee - optimizationRegFee : 0

        return {
          ...prev,
          offer: {
            ...prev.offer,
            collateral: {
              total: collateral,
            }
          }
        }
      })
    } else {
      setTaxFlowSilent((prev) => {
        const registrationFee = prev.registrationFee?.registrationFee ?? 0;

        if (!shouldCalculateB2CCollateral) {
          if (!prev.autoCalc?.collateralFee) {
            return prev;
          }
          const collateral = calcCollateralFee(registrationFee);

          return {
            ...prev,
            offer: {
              ...prev.offer,
              collateral: {
                ...prev.offer?.collateral,
                total: collateral
              }
            }
          }
        }

        const baseCollateral = prev.autoCalc?.collateralFee
          ? calcCollateralFee(registrationFee)
          : prev.offer?.collateral?.baseFee ?? 0;

        let b2cCollateral = prev.offer?.collateral?.b2cBreakdown ?? null;
        let b2cCollateralFee = prev.offer?.collateral?.b2cFee ?? 0;
        if (prev.autoCalc?.collateralFeeB2C) {
          b2cCollateral = calcWidgetRiskFee(registrationFee, isElectric)
          b2cCollateralFee = b2cCollateral?.total ?? 0
        }

        return {
          ...prev,
          offer: {
            ...prev.offer,
            collateral: {
              ...prev.offer?.collateral,
              total: baseCollateral + b2cCollateralFee,
              baseFee: baseCollateral,
              b2cFee: b2cCollateralFee,
              b2cBreakdown: b2cCollateral
            }
          }
        }
      })
    }
  }, [
    taxFlow.registrationFee?.registrationFee,
    taxFlow.offer?.collateral?.b2cFee,
    taxFlow.offer?.collateral?.baseFee,
    taxFlow.autoCalc?.collateralFeeB2C,
    taxFlow.autoCalc?.collateralFee,
    shouldCalculateB2CCollateral,
    isElectric,
    taxScenario?.flags?.optimization,
    taxCase.scenarios.offer?.calculation?.offer?.collateral?.total,
    taxCase.scenarios.offer?.calculation?.registrationFee?.registrationFee,
  ])


  useEffect(() => {
    const valueConfig = taxCase.taxCaseValueConfig?.whitelistedZeroTaxFeeDiscount
    if (isWhiteListedVehicle && taxFlow.autoCalc?.specialCaseFeeDiscount && valueConfig) {
      setTaxFlowSilent((prev) => ({
        ...prev,
        offer: {
          ...prev.offer,
          specialCaseFeeDiscount: safeNum(valueConfig?.value)
        }
      }))
    } else if (!isWhiteListedVehicle) {
      setTaxFlowSilent((prev) => ({
        ...prev,
        offer: {
          ...prev.offer,
          specialCaseFeeDiscount: 0
        }
      }))
    }
  }, [taxFlow.offer?.specialCaseFeeDiscount, taxFlow.autoCalc?.specialCaseFeeDiscount, isWhiteListedVehicle])

  // ---------- taxOffer totalPrice effect ----------
  useEffect(() => {
    const offer = taxFlow.offer;
    let sum = (offer?.caseFee ?? 0)
      + (offer?.collateral?.total ?? 0)
      + (offer?.licensePlateFee ?? 0)
      + (taxFlow?.registrationFee?.registrationFee ?? 0);
    // - (taxFlow.offer?.specialCaseFeeDiscount ?? 0);
    setTaxFlowSilent((prev) => ({
      ...prev,
      offer: {
        ...prev.offer,
        totalPrice: sum
      }
    }))
  }, [
    taxFlow.offer?.caseFee,
    taxFlow.offer?.licensePlateFee,
    taxFlow.offer?.collateral?.total,
    taxFlow.registrationFee?.registrationFee,
    taxFlow.offer?.specialCaseFeeDiscount
  ])

}

function handleTradePriceOverrides(output: Strict<TaxFlow>['tradePrice']['output'], overrides: Strict<TaxFlow>['tradePrice']['overrides']): Strict<TaxFlow>['tradePrice']['summary'] {
  let finalTradePrice = (output?.result?.tradePrice ?? 0);
  if (overrides?.mileage != undefined) {
    finalTradePrice -= (output?.result?.mileageRegulation ?? 0);
    finalTradePrice += (overrides.mileage ?? 0)
  }
  finalTradePrice += (overrides?.additionalRegulation?.amount ?? 0)

  return {
    additionalRegulation: overrides?.additionalRegulation?.amount ? Math.ceil(overrides?.additionalRegulation?.amount) : undefined,
    baseTradePrice: output?.result?.tradePrice ? Math.ceil(output?.result?.tradePrice) : undefined,
    finalTradePrice: Math.ceil(finalTradePrice),
  }
}

function effectiveNewPriceTotal(taxFlow: TaxFlow) {
  const newPriceType = taxFlow.newPrice?.newPriceType
  const newPriceWithoutEquipment = taxFlow.newPrice?.newPriceWithoutEquipment ?? 0;
  const equipmentPrice = taxFlow.newPrice?.equipmentPrice ?? 0;

  let newPrice = newPriceWithoutEquipment + equipmentPrice
  if (newPriceType === 'TAXABLE_VALUE') {
    newPrice = taxFlow.newPrice?.taxableValueNewPrice?.newPrice ?? 0
  }

  return newPrice;
}

function calculateCaseFee(registrationFee: number | null, dynamicFee: DynamicFee) {
  let { min, max, ratio } = dynamicFee;
  let fee = Math.round((registrationFee || 0) * ratio);
  if (fee <= min) {
    fee = min;
  } else if (fee >= max) {
    fee = max;
  }
  fee = Math.round(fee * 1.25);
  return fee
}

function calcCollateralFee(registrationFee: number) {
  // const shouldDisplayB2CCollateral = isWidgetCase && !taxCase.taxCaseValueConfig?.disableWidgetB2cCollateral.value
  const ratio = appConfig('tax.riskfee.standard.ratio')
  const minValue = appConfig('tax.riskfee.standard.minValue')

  let collateralFee = Math.round((registrationFee || 0) * ratio);
  /* If it is NOT a zero reg fee car, and the estimation is less than 500, then set it to 500 */
  if (registrationFee > 0 && collateralFee < minValue) {
    collateralFee = minValue
  }
  collateralFee = Math.round(collateralFee * 1.25); // VAT
  return collateralFee;
}

const calcWidgetRiskFee = (registrationFee: number, isElectric: boolean) => {
  // const isElectric = !taxCase.vehicle?.emissionAmount;
  const standard = calcStandardWidgetRiskFee(registrationFee || 0)
  const nonElectric = isElectric ? 0 : calcNonElectricWidgetRiskFee(registrationFee || 0)
  const electric = isElectric ? calcElectricWidgetRiskFee(registrationFee || 0) : 0
  return {
    total: Math.round((standard + nonElectric + electric) * 1.25),
    standard: Math.round(standard * 1.25),
    nonElectric: Math.round(nonElectric * 1.25),
    electric: Math.round(electric * 1.25)
  }
}

const calcStandardWidgetRiskFee = (registrationFee: number) => {
  const rate = appConfig("tax.riskfee.b2c.standard.ratio")
  const maxVal = appConfig("tax.riskfee.b2c.standard.maxValue")
  let standardN2CRiskFee = (registrationFee || 0) * rate
  if (standardN2CRiskFee > maxVal) {
    standardN2CRiskFee = maxVal
  }
  return standardN2CRiskFee
}

const calcNonElectricWidgetRiskFee = (registrationFee: number) => {
  const rate = appConfig("tax.riskfee.b2c.nonElectric.ratio")
  let nonElectricRiskFee = (registrationFee || 0) * rate
  return nonElectricRiskFee
}

const calcElectricWidgetRiskFee = (registrationFee: number) => {
  const rate = appConfig("tax.riskfee.b2c.electric.ratio");
  const minThreshhold = appConfig("tax.riskfee.b2c.electric.minThreshhold");
  let electricRiskFee = (registrationFee || 0) * rate
  if (registrationFee < minThreshhold) {
    electricRiskFee = 0;
  }
  return electricRiskFee
}
